import React, { FC, PropsWithChildren } from 'react';
import { Feather } from '@expo/vector-icons';
import { ScrollView, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { useContentMaxWidth } from '../../hooks/ui/useContentMaxWidth';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import DesignSystemButton from '../Button/DesignSystemButton';
import { ActivityInfo, ActivityType } from '../ActivityInfo/ActivityInfo';
import { COLORS } from '../../config/designSystem/Colors';
import { EvolunoClose } from '../../assets/images/Icons/Close/EvolunoClose';
import { HeroBackgroundActivity } from './HeroBackgroundActivity';
import { SPACING } from '../../config/designSystem/Spacing';
import { Title2 } from '../../config/designSystem/typography';

const HEADER_HEIGHT = 180;

interface ActivityPageTemplateProps extends PropsWithChildren {
  /** module gradient key for the hero */
  designSystemColor: string;
  /** hero illustration once wired */
  image?: string | number | null;
  /** raw activity type (audio/memo/journaling) selecting the hero scene */
  heroType?: string | null;
  /** shows the ⚙️ button over the hero (challenge settings) */
  onPressSettings?: () => void;
  title?: string | null;
  /** the type chip next to the title (figma: Activity Info) */
  activityType: ActivityType;
  onClose: () => void;
  ctaLabel?: string;
  onPressCta?: () => void;
  /** the CTA is shown greyed out (nothing to do right now) */
  ctaDisabled?: boolean;
}

/**
 * figma: ❖ Activity Page Template (Activities & Challenges pt1)
 * The scaffold of every activity page: Hero Background Activity with the
 * close overlay, title row with Activity Info, scrollable page content and
 * the bottom CTA.
 */
export const ActivityPageTemplate: FC<ActivityPageTemplateProps> = ({
  designSystemColor,
  image = null,
  heroType,
  onPressSettings,
  title,
  activityType,
  onClose,
  ctaLabel,
  onPressCta,
  ctaDisabled = false,
  children,
}) => {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  // readable on wide screens, the width following the screen (useContentMaxWidth)
  const contentMaxWidth = useContentMaxWidth();

  return (
    <View style={{ flex: 1, backgroundColor: COLORS.surface.contrast }}>
      <HeroBackgroundActivity
        designSystemColor={designSystemColor}
        activityType={heroType}
        image={image}
        width={width}
        height={HEADER_HEIGHT + insets.top}
      >
        <View style={{ position: 'absolute', top: insets.top + SPACING['sp-16'], left: SPACING['sp-16'], zIndex: 2 }}>
          <EvolunoClose size={SPACING['sp-48']} onPress={onClose} />
        </View>
        {!!onPressSettings && (
          <TouchableOpacity
            onPress={onPressSettings}
            style={{
              position: 'absolute',
              top: insets.top + SPACING['sp-16'],
              right: SPACING['sp-16'],
              zIndex: 2,
              width: SPACING['sp-48'],
              height: SPACING['sp-48'],
              borderRadius: SPACING['sp-48'] / 2,
              backgroundColor: COLORS.surface.contrast,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Feather name="settings" size={20} color={COLORS.content.accent} />
          </TouchableOpacity>
        )}
      </HeroBackgroundActivity>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          rowGap: SPACING['sp-16'],
          padding: SPACING['sp-16'],
          paddingTop: SPACING['sp-24'],
          width: '100%',
          maxWidth: contentMaxWidth,
          alignSelf: 'center',
        }}
      >
        {/* the title wraps; the type chip keeps its width and stays on screen */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', columnGap: SPACING['sp-8'] }}>
          <View style={{ flex: 1 }}>
            <Title2>{title}</Title2>
          </View>
          <View style={{ flexShrink: 0 }}>
            <ActivityInfo type={activityType} />
          </View>
        </View>
        {children}
      </ScrollView>
      <View
        style={{
          padding: SPACING['sp-16'],
          paddingBottom: insets.bottom + SPACING['sp-16'],
          width: '100%',
          maxWidth: contentMaxWidth,
          alignSelf: 'center',
        }}
      >
        {!!ctaLabel && (ctaDisabled || !!onPressCta) && (
          <DesignSystemButton
            label={ctaLabel}
            onPress={onPressCta || (() => undefined)}
            disabled={ctaDisabled}
          />
        )}
      </View>
    </View>
  );
};
